import React, { useState } from "react"
import { motion } from "framer-motion"
import {Send, CheckCircle, LogIn} from 'lucide-react'
import toast, { Toaster } from "react-hot-toast"
import { useAuth } from "../hooks/useAuth"
import { lumi } from "../lib/lumi"

const QuoteRequest = () => {
  const { isAuthenticated, user, signIn } = useAuth()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    equipmentType: "",
    condition: "new",
    message: ""
  })

  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      // Send email notification
      await lumi.tools.email.send({
        to: formData.email,
        subject: "Research Information Saved - MuleShed.com",
        fromName: "MuleShed.com Resource Center",
        html: `
          <h2>Your equipment research has been saved!</h2>
          <p>This is a confirmation of the information you've researched on MuleShed.com.</p>
          <h3>Research Details:</h3>
          <ul>
            <li><strong>Name:</strong> ${formData.name}</li>
            <li><strong>Email:</strong> ${formData.email}</li>
            <li><strong>Phone:</strong> ${formData.phone}</li>
            <li><strong>Company:</strong> ${formData.company || "N/A"}</li>
            <li><strong>Equipment:</strong> ${formData.equipmentType}</li>
            <li><strong>Condition:</strong> ${formData.condition}</li>
            <li><strong>Message:</strong> ${formData.message || "N/A"}</li>
          </ul>
          <p>Best regards,<br/>MuleShed.com Resource Center</p>
          <p style="color: #888; font-size: 12px; margin-top: 20px;">Disclaimer: MuleShed.com is an informational resource only and does not sell equipment or provide direct quotes. For purchasing inquiries, please contact authorized dealers directly.</p>
        `
      })
      
      toast.success("Research information saved! Check your email for a summary.")
      setSubmitted(true)
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          equipmentType: "",
          condition: "new",
          message: ""
        })
        setSubmitted(false)
      }, 3000)
    } catch (error) {
      toast.error("Failed to save information. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="bg-zinc-900 min-h-screen">
      <Toaster position="top-center" />
      
      {/* Header */}
      <section className="bg-zinc-950 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Pricing <span className="text-orange-500">Information</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Learn about typical <strong>shed mule cost</strong> ranges and <strong>mule shed delivery</strong> considerations. This resource helps you understand equipment investment factors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Understanding Equipment Costs
              </h2>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Whether researching new <strong>shed mule for sale</strong> options or certified <strong>used shed mule for sale</strong> units, understanding pricing factors, financing considerations, and delivery logistics is essential.
              </p>

              <div className="space-y-6">
                <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={20} />
                    Industry Standards
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Professional dealers typically respond to inquiries within 24-48 hours
                  </p>
                </div>

                <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={20} />
                    Configuration Options
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Equipment can often be customized to specific operational requirements
                  </p>
                </div>

                <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
                  <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={20} />
                    Financing Information
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Many dealers offer financing plans to accommodate different budgets
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quote Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {!isAuthenticated ? (
                <div className="bg-zinc-800 p-8 rounded-lg border border-zinc-700 text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">Sign In Required</h3>
                  <p className="text-gray-300 mb-6">
                    Sign in to save your equipment research and receive information summaries via email.
                  </p>
                  <button
                    onClick={signIn}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2 mx-auto"
                  >
                    <LogIn size={20} />
                    Sign In to Continue
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-zinc-800 p-8 rounded-lg border border-zinc-700">
                  <div className="mb-4 pb-4 border-b border-zinc-700">
                    <p className="text-gray-400 text-sm">
                      Signed in as: <span className="text-orange-500 font-bold">{user?.email}</span>
                    </p>
                  </div>
                  <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-white font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white font-medium mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-white font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="Your Company LLC"
                    />
                  </div>

                  <div>
                    <label htmlFor="equipmentType" className="block text-white font-medium mb-2">
                      Equipment Interest *
                    </label>
                    <select
                      id="equipmentType"
                      name="equipmentType"
                      value={formData.equipmentType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    >
                      <option value="">Select equipment type</option>
                      <option value="mule-628t">Mule 628T</option>
                      <option value="mule-pro-520">Mule Pro 520</option>
                      <option value="mule-heavy-750">Mule Heavy 750</option>
                      <option value="custom">Custom Configuration</option>
                      <option value="not-sure">Not Sure - Need Guidance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-2">
                      Condition Preference *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="condition"
                          value="new"
                          checked={formData.condition === "new"}
                          onChange={handleChange}
                          className="w-4 h-4 text-orange-500"
                        />
                        <span className="text-white">New Equipment</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="condition"
                          value="used"
                          checked={formData.condition === "used"}
                          onChange={handleChange}
                          className="w-4 h-4 text-orange-500"
                        />
                        <span className="text-white">Used Equipment</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors resize-none"
                      placeholder="Tell us about your hauling operation, capacity needs, or any specific requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitted || loading}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-green-500 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2"
                  >
                    {submitted ? (
                      <>
                        <CheckCircle size={24} />
                        Information Saved!
                      </>
                    ) : loading ? (
                      <>
                        Sending...
                      </>
                    ) : (
                      <>
                        Save Research Info
                        <Send size={20} />
                      </>
                    )}
                  </button>
                </div>
              </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              Understanding <span className="text-orange-500">Shed Mule Cost</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Investment in professional shed moving equipment pays dividends through increased productivity, safety, and business capacity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <h3 className="text-2xl font-bold text-white mb-4">Entry Level</h3>
              <p className="text-gray-400 mb-4">
                Certified pre-owned units perfect for growing operations
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>• Used Mule Pro 520</li>
                <li>• Lower initial investment</li>
                <li>• Full inspection & warranty</li>
                <li>• Financing available</li>
              </ul>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg border-2 border-orange-500">
              <div className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Professional</h3>
              <p className="text-gray-400 mb-4">
                New flagship Mule 628T for established haulers
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>• New Mule 628T</li>
                <li>• Full manufacturer warranty</li>
                <li>• Latest technology & features</li>
                <li>• Premium support package</li>
              </ul>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <h3 className="text-2xl font-bold text-white mb-4">Heavy-Duty</h3>
              <p className="text-gray-400 mb-4">
                Maximum capacity for the largest structures
              </p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>• New Mule Heavy 750</li>
                <li>• 35,000 lbs capacity</li>
                <li>• Extended boom reach</li>
                <li>• Commercial-grade build</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default QuoteRequest
